import React, { useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Paper,
  useMediaQuery,
  useTheme,
  List,
  ListItemButton,
  ListItemText,
  Popper, 
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSearchParams } from 'react-router-dom';
import { logSearch } from '../../firebase';

const SearchBar = ({ displayCategoriesSelector }) => {
  const theme = useTheme();
  const formRef = useRef(null); 
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const searchEventQuery = searchParams.get('search_event');

  const [suggestions, setSuggestions] = useState([]);
  const [searchText, setSearchText] = useState(searchEventQuery || '');
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, setValue } = useForm({
    defaultValues: { searchQuery: searchEventQuery || '' },
  });

  const onSearchSubmit = (values) => {
    if (values.searchQuery) logSearch(values.searchQuery);
    setSearchParams({ search_event: values.searchQuery });
    setSuggestions([]);
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!searchText || searchText.length < 2) {
        setSuggestions([]);
        return;
      }
      setLoading(true);
      try {
        const response = await fetch(
          `https://api.oasisapp.mx/events/all/suggestions?query=${encodeURIComponent(searchText)}`
        );
        const data = await response.json();
        if (data.success && Array.isArray(data.suggestions)) {
          setSuggestions(data.suggestions);
        } else {
          setSuggestions([]);
        }
      } catch (e) {
        console.error('Error obteniendo sugerencias:', e);
      } finally {
        setLoading(false);
      }
    };

    const t = setTimeout(fetchSuggestions, 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const handleSelectSuggestion = (suggestion) => {
    setValue('searchQuery', suggestion);
    setSearchText(suggestion);
    setSuggestions([]);
    onSearchSubmit({ searchQuery: suggestion });
  };

  return (
    <Box
      ref={formRef} // 👈 ancla del Popper
      onSubmit={handleSubmit(onSearchSubmit)}
      component="form"
      sx={{
        bgcolor: 'common.white',
        borderRadius: 2,
        position: 'relative',
        zIndex: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
        <InputBase
          {...register('searchQuery')}
          sx={{ ml: 2, flex: 2, color: 'grey.800', bgcolor: 'common.white' }}
          placeholder="Busca aquí un evento"
          inputProps={{ 'aria-label': 'search event' }}
          value={searchText}
          onChange={(e) => {
            const value = e.target.value;
            setSearchText(value);
            setValue('searchQuery', value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit(onSearchSubmit)();
            }
          }}
        />
        <IconButton type="submit" aria-label="search" sx={{ mr: 1, my: 0.5 }}>
          <SearchIcon sx={{ color: 'secondary.main' }} />
        </IconButton>

        {displayCategoriesSelector && !isMobile && (
          <>
            <Divider
              flexItem
              sx={{ my: 1.5, mx: 0.5, color: 'grey.500' }}
              orientation="vertical"
            />
            <FormControl
              color="secondary"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-notchedOutline': { border: 0 },
                '& svg': {
                  color: 'common.white',
                  borderRadius: '50%',
                  backgroundColor: 'secondary.main',
                },
              }}
            >
              <Select
                {...register('category')}
                sx={{ color: 'grey.800' }}
                color="secondary"
                defaultValue="allCategories"
                id="category-select"
              >
                <MenuItem value="allCategories">Todas las categorías</MenuItem>
                <MenuItem value="1">Fútbol</MenuItem>
                <MenuItem value="2">Béisbol</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
      </Box>

      {/* 🔽 SUGERENCIAS con Popper */}
      <Popper
        open={suggestions.length > 0}
        anchorEl={formRef.current}
        placement="bottom-start"
        style={{ zIndex: 99999 }} // por encima de banners/carruseles
      >
        <Paper
          elevation={6}
          sx={{
            mt: 1,
            borderRadius: 2,
            maxHeight: 250,
            overflowY: 'auto',
            bgcolor: 'grey.900',
            color: 'common.white',
            // ancho igual al form para que calce debajo del input
            width: formRef.current?.offsetWidth || 400,
          }}
        >
          <List>
            {suggestions.map((sug, idx) => (
              <ListItemButton
                key={idx}
                onClick={() => handleSelectSuggestion(sug)}
                sx={{
                  '&:hover': { bgcolor: 'secondary.main', color: 'common.white' },
                }}
              >
                <ListItemText primary={sug} />
              </ListItemButton>
            ))}
            {loading && (
              <ListItemText
                primary="Cargando..."
                sx={{ textAlign: 'center', py: 1 }}
              />
            )}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
};

SearchBar.defaultProps = { displayCategoriesSelector: false };

SearchBar.propTypes = { displayCategoriesSelector: PropTypes.bool };

export default SearchBar;
